<template>
	<div class="event-best-reward">
		<div class="event-best-reward-left">
			<div class="event-best-reward-left-title">
				{{ `${translate("event.best_reward")}:` }}
			</div>
			<div
				class="event-best-reward-left-building"
				v-if="buildingFrameName"
			>
				{{ translate(`${buildingFrameName}.name`) }}
			</div>

			<Tooltip
				:heading="translate(`${rewardInfo.entity?.frameName}.name`)"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<AppIconButton
						class="income-box__info"
						:type="IconTypes.INFO"
					/>
				</template>
				<template #tooltip>
					<div class="reward-tooltip">
						<p
							v-html="
								rewardInfo.entity?.description.replace(
									/\{\/?b\}/g,
									'',
								)
							"
						/>
						<TileAggregationDetails
							v-if="rewardInfo.entity"
							:entity="rewardInfo.entity"
							:isPromotion="true"
						/>
					</div>
				</template>
			</Tooltip>
		</div>
		<div class="event-best-reward-right" v-if="rewardInfo.count > 0">
			{{ `${rewardInfo.count}X` }}
		</div>
	</div>
</template>

<script setup lang="ts">
import AppIconButton from "@/components/AppIconButton.vue";
import TileAggregationDetails from "@/components/TileAggregationDetails.vue";
import Tooltip from "@/components/Tooltip.vue";
import { EVENTS_WITH_MILESTONES } from "@/config/constants";
import { useEventStore } from "@/event";
import { FrameName } from "@/game/entities/config";
import type TileEntity from "@/game/entities/TileEntity";
import useTileRepository from "@/hooks/useTileRepository";
import type { MilestoneItem } from "@/types/event";
import { EventCategory, IconTypes, Placements } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw } from "vue";

const eventStore = useEventStore();
const { attributes, milestones } = storeToRefs(eventStore);
const tilerepo = markRaw(useTileRepository());

const buildingFrameName = computed((): FrameName | null => {
	if (attributes.value.type === EventCategory.EASTER) {
		return FrameName.RABBIT_FARM;
	}

	if (attributes.value.type === EventCategory.FISHING) {
		return FrameName.FORTRESS;
	}
	if (attributes.value.type === EventCategory.FIREFEST) {
		return FrameName.ENKINDLED_HOUSEHOLD;
	}
	if (attributes.value.type === EventCategory.SOLAR) {
		return FrameName.SOLSTICE_WINDMILL;
	}
	if (attributes.value.type === EventCategory.HUNTING) {
		return FrameName.SHEPHERDS_LODGE;
	}
	if (attributes.value.type === EventCategory.HALLOWEEN) {
		return FrameName.HAUNTED_MANSION;
	}
	if (attributes.value.type === EventCategory.PIRATES) {
		return FrameName.CORSAIR_SHIP;
	}

	return null;
});

const rewardInfo = computed(
	(): { entity: TileEntity | null; count: number } => {
		let totalRewardAmount = 0;
		let totalPremiumAmount = 0;

		if (
			milestones.value &&
			buildingFrameName.value &&
			attributes.value.type &&
			EVENTS_WITH_MILESTONES.includes(attributes.value.type)
		) {
			milestones.value.forEach((value: MilestoneItem): void => {
				if (
					value.reward &&
					"frameName" in value.reward &&
					value.reward?.frameName === buildingFrameName.value
				) {
					totalRewardAmount += value.reward.amount as number;
				}
				if (
					value.premium?.reward &&
					"frameName" in value.premium.reward &&
					value.premium?.reward?.frameName === buildingFrameName.value
				) {
					totalPremiumAmount += value.premium.reward.amount as number;
				}
			});
		}

		return {
			entity: buildingFrameName.value
				? tilerepo.createEntityByFrameName(buildingFrameName.value)
				: null,
			count: totalRewardAmount + totalPremiumAmount,
		};
	},
);
</script>

<style lang="scss" scoped>
.event-best-reward {
	position: absolute;
	bottom: 0rem;
	color: #fff3dd;
	@include flex-row-between;
	@include font-size(13);
	align-items: end;
	padding: 0 0.5rem;
	padding-bottom: 0.5rem;
	width: 100%;
	height: 100%;
	&-left {
		&-title {
			color: #f4d278;
			@include font-size(16);
			font-weight: bold;
			text-transform: uppercase;
		}
		&-building {
			margin-bottom: 0.25rem;
			font-weight: bold;
			text-transform: uppercase;
		}
	}
	&-right {
		filter: drop-shadow(0.088rem 0.088rem rgba(0, 0, 0, 0.8));
		@include font-size(60);
		background:
			linear-gradient(to top, #f4d278, #f2dcb5),
			linear-gradient(to bottom, #fff3dd, #fff3dd);
		background-clip: text;
		-webkit-background-clip: text;
		color: #fff3dd;
		-webkit-text-fill-color: transparent;
		line-height: 4rem;
	}
}
</style>
